import { Layout } from './CoreComponents';
import BaseGrid from './Shared/BaseGrid';
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { getPORequests, getPORequestUserList } from '../store/slices/poRequestSlice';
import { RootState, AppDispatch } from '../store/index';
import { type ColDef, type ICellRendererParams } from 'ag-grid-community';
import { USER_ROLES } from '../constants';
import { getRoleFromToken, getUserIdFromToken } from '../utils/tokenUtils';
import { formatDate } from '../utils/formatterDate';
import { useExpandedBatches } from '../hooks/useExpandedBatches';

const RequestTrackingPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    items: requests,
    loading,
    poRequestUserList,
    operationLoading
  } = useSelector((state: RootState) => state.poRequest);
  
  const { user, token } = useSelector((state: RootState) => state.auth);
  
  // Get role and user ID from token (more secure than localStorage)
  // Fallback to Redux state if token decode fails
  const tokenRole = getRoleFromToken(token);
  const tokenUserId = getUserIdFromToken(token);
  
  // Use token data if available, otherwise fallback to Redux state
  const userRole = tokenRole || user?.role;
  const currentUserId = tokenUserId || user?.id;
  
  // Check if user role is 'user'
  const isUserRole = userRole === USER_ROLES.USER;
  
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [dateFilter, setDateFilter] = useState(searchParams.get('days') || '');
  const [expandedBatches, setExpandedBatches] = useExpandedBatches('requestTracking_expandedBatches');

  // Filter requests client-side based on column search, status and date
  const filteredRequests = requests.filter(request => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesId = (request.id?.toString() || '').toLowerCase().includes(searchLower);
      const matchesItemCode = (request.itemCode?.toLowerCase() || '').includes(searchLower);
      const matchesShortCode = (request.shortCode?.toLowerCase() || '').includes(searchLower);
      const matchesItemName = (request.itemName?.toLowerCase() || '').includes(searchLower);
      if (!matchesId && !matchesItemCode && !matchesShortCode && !matchesItemName) return false;
    }

    // Status filter
    let matchesStatus = true;
    if (statusFilter) {
      matchesStatus = request.requestStatus === statusFilter;
    }

    // Date filter (last N days)
    let matchesDate = true;
    if (dateFilter) {
      const days = parseInt(dateFilter);
      if (!isNaN(days) && request.createdAt) {
        const createdDate = new Date(request.createdAt);
        const diffTime = Math.abs(Date.now() - createdDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        matchesDate = diffDays <= days;
      }
    }
    
    return matchesStatus && matchesDate;
  });

  // Group requests by batchId
  const groupedByBatch = filteredRequests.reduce((acc, request) => {
    const batchId = (request as any).batchId || 0;
    if (!acc[batchId]) {
      acc[batchId] = [];
    }
    acc[batchId]!.push(request);
    return acc;
  }, {} as Record<number, typeof requests>);

  const sortedBatchEntries = Object.entries(groupedByBatch).sort(([, a], [, b]) => {
    const dateA = a[0]?.createdAt ? new Date(a[0].createdAt).getTime() : 0;
    const dateB = b[0]?.createdAt ? new Date(b[0].createdAt).getTime() : 0;
    return dateB - dateA;
  });

  // Toggle batch expansion
  const toggleBatch = (batchId: number) => {
    setExpandedBatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batchId)) {
        newSet.delete(batchId);
      } else {
        newSet.add(batchId);
      }
      return newSet;
    });
  };

  // Toggle all batches
  const allBatchIds = Object.keys(groupedByBatch).map(id => parseInt(id));
  const isAllExpanded = allBatchIds.length > 0 && allBatchIds.every(id => expandedBatches.has(id));

  const toggleAllBatches = () => {
    if (isAllExpanded) {
      setExpandedBatches(new Set());
    } else {
      setExpandedBatches(new Set(allBatchIds));
    }
  };

  // Handle column sort
  // Get status badge class
  const getStatusBadgeClass = (status: string | null | undefined) => {
    if (!status) return 'text-gray-600 dark:text-gray-400';
    const statusLower = status.toLowerCase();
    if (statusLower === 'pending') return 'text-yellow-600 dark:text-yellow-400';
    if (statusLower === 'approved' || statusLower.includes('ready') || statusLower.includes('evaluate')) return 'text-green-600 dark:text-green-400';
    if (statusLower === 'rejected') return 'text-red-600 dark:text-red-400';
    if (statusLower.includes('quote') || statusLower.includes('converted') || statusLower.includes('po')) return 'text-blue-600 dark:text-blue-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const getStatusText = (status: string | null | undefined) => {
    if (!status) return '-';
    const statusMap: Record<string, string> = {
      'Pending': 'Beklemede',
      'Approved': 'Onaylandı',
      'Rejected': 'Reddedildi',
      'ReadyToEvaluate': 'Değerlendirmeye Hazır',
      'ConvertedToQuote': 'Teklife Dönüştürüldü',
      'ConvertedToPO': 'Satın alma siparişi oluşturuldu',
      'Draft': 'Taslak'
    };
    return statusMap[status] || status;
  };

  // Fetch user list when component mounts (only if not user role)
  useEffect(() => {
    if (!isUserRole) {
      dispatch(getPORequestUserList(true)); // Cache busting enabled
    }
  }, [dispatch, isUserRole]);

  // Fetch requests when component mounts or filters change
  useEffect(() => {
    const params: any = {
      _t: Date.now() // Cache busting
    };

    if (isUserRole && currentUserId) {
      params.CreatedBy = currentUserId;
    } else if (selectedUser) {
      params.CreatedBy = parseInt(selectedUser, 10);
    }

    dispatch(getPORequests(params));
  }, [dispatch, selectedUser, isUserRole, currentUserId]);

  const childColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: 'ID',
      field: 'id',
      minWidth: 60,
      flex: 0.4,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Kodu',
      field: 'itemCode',
      minWidth: 100,
      flex: 0.8,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Kök Kod',
      valueGetter: (params) => params.data?.rootCode || '-',
      minWidth: 80,
      flex: 0.6,
    },
    {
      headerName: 'Kısa Kod',
      field: 'shortCode',
      minWidth: 70,
      flex: 0.5,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Ürün Adı - Birim',
      minWidth: 200,
      flex: 1.5,
      tooltipValueGetter: (params) => params.data?.itemName ? `${params.data.itemName} - ${params.data?.unit || 'AD'}` : '-',
      valueGetter: (params) => params.data?.itemName ? `${params.data.itemName} - ${params.data?.unit || 'AD'}` : '-',
    },
    {
      headerName: 'Kategori',
      field: 'categoryName',
      minWidth: 120,
      flex: 1,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Talep Mikt.',
      field: 'requestedQuantity',
      minWidth: 80,
      flex: 0.6,
      type: 'rightAligned',
      valueFormatter: (params) => params.value ? Number(params.value).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '-',
    },
    {
      headerName: 'Onay Mikt.',
      field: 'approvedQuantity',
      minWidth: 80,
      flex: 0.6,
      type: 'rightAligned',
      valueFormatter: (params) => params.value !== null && params.value !== undefined ? Number(params.value).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '-',
    },
    {
      headerName: 'Teslim Tarihi',
      field: 'requestedDeliveryDate',
      minWidth: 100,
      flex: 0.7,
      valueFormatter: (params) => params.value ? formatDate(params.value) : '-',
    },
    {
      headerName: 'Durum',
      field: 'requestStatus',
      minWidth: 100,
      flex: 0.7,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return '-';
        return (
          <span className={`text-xs font-medium ${getStatusBadgeClass(params.value)}`}>
            {getStatusText(params.value)}
          </span>
        );
      },
    },
    {
      headerName: 'Değerlendirme',
      field: 'evaluationResult',
      minWidth: 120,
      flex: 0.8,
      valueFormatter: (params) => params.value || '-',
    },
    {
      headerName: 'Not',
      valueGetter: (params) => params.data?.note ?? params.data?.Note ?? '',
      minWidth: 120,
      flex: 0.8,
      tooltipValueGetter: (params) => params.data?.note ?? params.data?.Note ?? '',
      valueFormatter: (params) => params.value || '-',
    },
  ], []);

  return (
    <Layout currentScreen="request-tracking">
      <div className="max-w-8xl mx-auto h-full flex flex-col">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4 transition-colors duration-300 flex-shrink-0">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-end gap-3">
            {/* Search bar - dinamik genişlik */}
            <div className="flex-1 min-w-[300px]">
              <input 
                type="text" 
                placeholder="Talep No, Ürün Kodu, Kısa Kod veya Adı ile Ara..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 text-sm" 
              />
            </div>

            {/* Filtreler ve Yenile */}
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[150px]"
              >
                <option value="">Tüm Durumlar</option>
                <option value="Pending">Beklemede</option>
                <option value="Approved">Onaylandı</option>
                <option value="Rejected">Reddedildi</option>
                <option value="ReadyToEvaluate">Değerlendirmeye Hazır</option>
                <option value="ConvertedToQuote">Teklife Dönüştürüldü</option>
                <option value="ConvertedToPO">Satın Alma Siparişi</option>
                <option value="Draft">Taslak</option>
              </select>
              <select 
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[150px]"
              >
                <option value="">Tüm Zamanlar</option>
                <option value="7">Son 7 Gün</option>
                <option value="30">Son 30 Gün</option>
                <option value="90">Son 3 Ay</option>
              </select>
              {!isUserRole && (
                <select 
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  disabled={operationLoading.poRequestUserList}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
                >
                  <option value="">Tüm Kullanıcılar</option>
                  {operationLoading.poRequestUserList ? (
                    <option value="" disabled>Yükleniyor...</option>
                  ) : (
                    poRequestUserList && Array.isArray(poRequestUserList) && poRequestUserList.length > 0 ? (
                      poRequestUserList.map((user: any) => (
                        <option key={user.id} value={user.id}>
                          {user.fullName || user.email || `User ${user.id}`}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>Kullanıcı bulunamadı</option>
                    )
                  )}
                </select>
              )}
              <button 
                onClick={() => {
                  const params: any = { _t: Date.now() };
                  if (isUserRole && currentUserId) params.CreatedBy = currentUserId;
                  else if (selectedUser) params.CreatedBy = parseInt(selectedUser, 10);
                  dispatch(getPORequests(params));
                }}
                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors text-sm h-[38px] flex items-center justify-center min-w-[100px]"
              >
                <i className="fas fa-sync-alt mr-2"></i>Yenile
              </button>
            </div>
            </div>
          </div>
        </div>

        {/* Expandable Grid */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300 flex flex-col min-h-0 flex-1">
          {loading ? (
            <div className="px-6 py-12 text-center">
              <div className="text-gray-500 dark:text-gray-400">
                <i className="fas fa-spinner fa-spin text-4xl mb-4"></i>
                <p className="text-sm">Yükleniyor...</p>
              </div>
            </div>
          ) : Object.keys(groupedByBatch).length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                <i className="fas fa-inbox text-4xl mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Henüz veri bulunmuyor
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm || selectedUser 
                    ? 'Arama kriterlerinize uygun veri bulunamadı.'
                    : 'Sistemde henüz veri bulunmuyor.'
                  }
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Card Layout */}
              <div className="lg:hidden overflow-auto min-h-0 flex-1">
                <div className="p-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{Object.keys(groupedByBatch).length} Parti</span>
                  <button
                    onClick={toggleAllBatches}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  >
                    {isAllExpanded ? 'Hepsini Daralt' : 'Hepsini Genişlet'}
                  </button>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedBatchEntries.map(([batchIdStr, batchRequests]) => {
                    const batchId = parseInt(batchIdStr);
                    const isExpanded = expandedBatches.has(batchId);
                    const firstRequest = batchRequests[0];
                    const uniqueCategories = Array.from(new Set(batchRequests.map(r => r.categoryName).filter(Boolean))).join(', ');
                    const itemCount = batchRequests.length;

                    if (!firstRequest) return null;

                    return (
                      <div key={batchId}>
                        <div
                          className="px-4 py-3 flex items-center gap-3 bg-gray-50 dark:bg-gray-700/50 cursor-pointer active:bg-gray-100 dark:active:bg-gray-700 transition-colors"
                          onClick={() => toggleBatch(batchId)}
                        >
                          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-xs text-gray-500 dark:text-gray-400 w-4`}></i>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">Parti #{batchId || '-'}</span>
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">{itemCount} Kalem</span>
                            </div>
                            <p className="text-xs text-blue-600 dark:text-blue-400 truncate mt-0.5">{uniqueCategories || '-'}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs font-medium text-gray-700 dark:text-gray-300">{firstRequest.createdByName || `User ${firstRequest.createdBy}`}</div>
                            <div className="text-[10px] text-gray-500 dark:text-gray-400">{firstRequest.createdAt ? formatDate(firstRequest.createdAt) : '-'}</div>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="bg-white dark:bg-gray-800">
                            {batchRequests.map((req) => (
                              <div key={req.id} className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/50">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{req.itemName || '-'}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{req.itemCode || '-'} {req.shortCode ? `· ${req.shortCode}` : ''}</p>
                                  </div>
                                  <span className={`text-xs font-medium ml-2 flex-shrink-0 ${getStatusBadgeClass(req.requestStatus)}`}>
                                    {getStatusText(req.requestStatus)}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Kategori:</span>
                                    <span className="text-gray-700 dark:text-gray-300">{req.categoryName || '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Birim:</span>
                                    <span className="text-gray-700 dark:text-gray-300">{(req as any).unit || 'AD'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Talep:</span>
                                    <span className="text-gray-700 dark:text-gray-300">{req.requestedQuantity ? Number(req.requestedQuantity).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Onay:</span>
                                    <span className="text-gray-700 dark:text-gray-300">{req.approvedQuantity != null ? Number(req.approvedQuantity).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '-'}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 dark:text-gray-400">Teslim:</span>
                                    <span className="text-gray-700 dark:text-gray-300">{req.requestedDeliveryDate ? formatDate(req.requestedDeliveryDate) : '-'}</span>
                                  </div>
                                  {req.evaluationResult && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500 dark:text-gray-400">Değerl.:</span>
                                      <span className="text-gray-700 dark:text-gray-300">{req.evaluationResult}</span>
                                    </div>
                                  )}
                                </div>
                                {((req as any).note || (req as any).Note) && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic truncate">Not: {(req as any).note ?? (req as any).Note}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Desktop Table + Grid Layout */}
              <div className="hidden lg:block overflow-auto min-h-0 flex-1">
                <table className="w-full border border-gray-300 dark:border-gray-600" style={{ tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center text-[11px] font-medium text-gray-700 dark:text-gray-300" style={{ width: '36px' }}>
                        <button 
                          onClick={toggleAllBatches}
                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors focus:outline-none"
                          title={isAllExpanded ? "Hepsini Daralt" : "Hepsini Genişlet"}
                        >
                          <i className={`fas fa-angle-double-${isAllExpanded ? 'up' : 'down'} text-xs`}></i>
                        </button>
                      </th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300" style={{ width: '72px' }}>Parti No</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300">Özet</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-gray-300">Oluşturan - Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBatchEntries.map(([batchIdStr, batchRequests]) => {
                      const batchId = parseInt(batchIdStr);
                      const isExpanded = expandedBatches.has(batchId);
                      const firstRequest = batchRequests[0];
                      const uniqueCategories = Array.from(new Set(batchRequests.map(r => r.categoryName).filter(Boolean))).join(', ');
                      const itemCount = batchRequests.length;
                      
                      if (!firstRequest) return null;
                      
                      return (
                        <React.Fragment key={batchId}>
                          {/* Parent Row */}
                          <tr
                            className="bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            onClick={() => toggleBatch(batchId)}
                          >
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-center">
                              <i 
                                className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} text-[10px] text-gray-500 dark:text-gray-400`}
                              ></i>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs font-semibold text-gray-800 dark:text-gray-100">
                              {batchId || '-'}
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs text-gray-800 dark:text-gray-100">
                              <div className="flex flex-col leading-tight">
                                <span className="font-medium text-gray-700 dark:text-gray-200">{uniqueCategories || '-'}</span>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">{itemCount} Kalem</span>
                              </div>
                            </td>
                            <td className="border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs text-gray-800 dark:text-gray-100">
                              <div className="flex flex-col leading-tight">
                                <div className="font-medium text-gray-700 dark:text-gray-200">{firstRequest.createdByName || `User ${firstRequest.createdBy}`}</div>
                                <div className="text-[10px] text-gray-500 dark:text-gray-400">{firstRequest.createdAt ? formatDate(firstRequest.createdAt) : '-'}</div>
                              </div>
                            </td>
                          </tr>
                          
                          {/* Child Grid - Expanded */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={4} className="border border-gray-300 dark:border-gray-600 p-0">
                                <div className="ag-child-grid">
                                  <BaseGrid
                                    gridStateKey="ag-grid-request-tracking-child"
                                    rowData={batchRequests}
                                    columnDefs={childColumnDefs}
                                    getRowId={(params) => String(params.data.id)}
                                    suppressNoRowsOverlay={true}
                                    autoSizeStrategy={{ type: 'fitGridWidth' }}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RequestTrackingPage;

